import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'

interface PoolResult {
  crewProfileId: string
  userId: string
  displayName: string
  primaryRole: string
  baseCity: string
  baseCountry: string
  distanceKm: number | null
  averageRating: number | null
  showsForArtistCount: number
  dayRateMinor: number | null
  internalReviewCount: number
  externalReviewCount: number
  internalReviewAverage: number | null
  externalReviewAverage: number | null
}

interface TourOption {
  id: string
  name: string
  startDate: string | null
  endDate: string | null
}

interface TourCrewNeed {
  id: string
  tourId: string | null
  role: string
  label: string | null
  dateFrom: string
  dateTo: string
  venueCity: string | null
  matchCount?: number
}

interface Candidate {
  id: string
  crewProfileId: string
  status: 'shortlisted' | 'interview_requested' | 'interested' | 'declined'
}

interface OpenRequirement {
  id: string
  showId: string
  showTitle: string
  showCity: string
  showDate: string
  role: string
}

const ROLES = [
  'foh_engineer', 'monitor_engineer', 'sound_tech', 'lighting_director',
  'lighting_operator', 'spotlight_operator', 'stage_manager', 'backline_tech',
  'guitar_tech', 'drum_tech', 'tour_manager', 'production_manager',
  'wardrobe_supervisor', 'hair_makeup', 'medic', 'rigger', 'stagehand',
  'truck_driver', 'backing_singer', 'band_musician', 'musical_director',
] as const

function candidateStatusLabel(status: Candidate['status'] | undefined) {
  switch (status) {
    case 'interested':
      return 'Interested'
    case 'declined':
      return 'Declined'
    case 'interview_requested':
      return 'Interview sent'
    case 'shortlisted':
      return 'Shortlisted'
    default:
      return null
  }
}

function formatReview(count: number, average: number | null) {
  if (count === 0) return '—'
  if (average == null) return `${count}`
  return `${count} · ${average.toFixed(1)}`
}

export function TalentPoolPage() {
  const queryClient = useQueryClient()
  const [role, setRole] = useState<string>('band_musician')
  const [label, setLabel] = useState('')
  const [tourId, setTourId] = useState('')
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().slice(0, 10))
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10))
  const [searchKey, setSearchKey] = useState(0)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)

  const tours = useQuery<TourOption[], ApiClientError>({
    queryKey: ['tours-list'],
    queryFn: () => artistFetch<TourOption[]>('/tours'),
  })

  const poolPublished = useQuery<{ need: TourCrewNeed; matches: PoolResult[] }, ApiClientError>({
    queryKey: ['pool-crew-need', searchKey, role, dateFrom, dateTo],
    queryFn: () =>
      artistFetch<{ need: TourCrewNeed; matches: PoolResult[] }>('/crew/pool/needs', {
        method: 'POST',
        body: { role, dateFrom, dateTo },
      }),
    enabled: searchKey > 0 && !tourId,
  })

  const published = useQuery<{ need: TourCrewNeed; matches: PoolResult[] }, ApiClientError>({
    queryKey: ['tour-crew-need', searchKey, tourId, role],
    queryFn: () =>
      artistFetch<{ need: TourCrewNeed; matches: PoolResult[] }>('/crew/tour-needs', {
        method: 'POST',
        body: {
          tourId,
          role,
          ...(label.trim() ? { label: label.trim() } : {}),
        },
      }),
    enabled: searchKey > 0 && Boolean(tourId),
  })

  const activeNeedId = tourId ? published.data?.need.id : poolPublished.data?.need.id

  const candidates = useQuery<Candidate[], ApiClientError>({
    queryKey: ['tour-crew-candidates', activeNeedId],
    queryFn: () => artistFetch<Candidate[]>(`/crew/tour-needs/${activeNeedId}/candidates`),
    enabled: !!activeNeedId,
  })

  const poolRequirements = useQuery<OpenRequirement[], ApiClientError>({
    queryKey: ['pool-requirements', searchKey, role, dateFrom, dateTo],
    queryFn: () =>
      artistFetch<OpenRequirement[]>(
        `/crew/pool/requirements?role=${role}&dateFrom=${dateFrom}&dateTo=${dateTo}`,
      ),
    enabled: searchKey > 0 && !tourId,
  })

  const tourRequirements = useQuery<OpenRequirement[], ApiClientError>({
    queryKey: ['tour-requirements', activeNeedId],
    queryFn: () => artistFetch<OpenRequirement[]>(`/crew/tour-needs/${activeNeedId}/requirements`),
    enabled: !!activeNeedId,
  })

  const candidateMutation = useMutation({
    mutationFn: (input: {
      needId: string
      crewProfileId: string
      action: 'shortlist' | 'request_interview'
    }) =>
      artistFetch<Candidate>(`/crew/tour-needs/${input.needId}/candidates`, {
        method: 'POST',
        body: {
          crewProfileId: input.crewProfileId,
          action: input.action,
        },
      }),
    onSuccess: (_, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['tour-crew-candidates', vars.needId] })
      setStatusMessage(
        vars.action === 'request_interview'
          ? 'Interview request sent — crew will be notified.'
          : 'Crew member shortlisted.',
      )
    },
  })

  const offerMutation = useMutation({
    mutationFn: async (input: {
      crewProfileId: string
      requirementId?: string
    }) => {
      if (activeNeedId && tourId) {
        return artistFetch(`/crew/tour-needs/${activeNeedId}/offers`, {
          method: 'POST',
          body: input,
        })
      }
      if (!input.requirementId) {
        throw new Error('Pick a show slot before sending an offer.')
      }
      return artistFetch('/crew/pool/offers', {
        method: 'POST',
        body: input,
      })
    },
    onSuccess: () => {
      setStatusMessage('Role offer sent to crew member.')
    },
  })

  const rows = tourId ? (published.data?.matches ?? []) : (poolPublished.data?.matches ?? [])
  const activeError = tourId ? published.error : poolPublished.error
  const activeLoading = tourId ? published.isLoading : poolPublished.isLoading
  const requirements = tourId ? (tourRequirements.data ?? []) : (poolRequirements.data ?? [])

  const candidateByCrew = new Map(
    (candidates.data ?? []).map((item) => [item.crewProfileId, item]),
  )

  function onSearch(e: FormEvent) {
    e.preventDefault()
    setStatusMessage(null)
    setSearchKey((k) => k + 1)
    if (tourId) {
      void published.refetch()
    } else {
      void poolPublished.refetch()
    }
  }

  async function handleSendOffer(crewProfileId: string) {
    setStatusMessage(null)
    try {
      if (requirements.length === 0) {
        setStatusMessage('No open role slots found for this search.')
        return
      }
      if (requirements.length === 1) {
        await offerMutation.mutateAsync({
          crewProfileId,
          requirementId: requirements[0]!.id,
        })
        return
      }
      const options = requirements
        .map(
          (req, index) =>
            `${index + 1}. ${req.showTitle} (${req.showCity}, ${new Date(req.showDate).toLocaleDateString()})`,
        )
        .join('\n')
      const picked = window.prompt(
        `Multiple open slots — enter the number to offer:\n\n${options}`,
        '1',
      )
      const index = Number(picked) - 1
      if (!Number.isInteger(index) || index < 0 || index >= requirements.length) return
      await offerMutation.mutateAsync({
        crewProfileId,
        requirementId: requirements[index]!.id,
      })
    } catch (err) {
      setStatusMessage(err instanceof Error ? err.message : 'Could not send offer.')
    }
  }

  return (
    <div>
      <PageHeader
        title="Talent pool"
        subtitle="Search all Ovation crew by role, availability, and location. Shortlist matches, request interviews, or send offers."
      />

      <form
        onSubmit={onSearch}
        className="mb-6 flex flex-wrap items-end gap-3 rounded-lg border border-ovation-ink/10 bg-white p-4"
      >
        <div className="min-w-[200px]">
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
            Tour (optional)
          </label>
          <select
            value={tourId}
            onChange={(e) => setTourId(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          >
            <option value="">Manual date search</option>
            {(tours.data ?? []).map((tour) => (
              <option key={tour.id} value={tour.id}>
                {tour.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
            Role
          </label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-[180px]">
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
            Label (optional)
          </label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Lead bass guitarist"
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          />
        </div>
        {!tourId ? (
          <>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
                From
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
                To
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
              />
            </div>
          </>
        ) : null}
        <button
          type="submit"
          className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
        >
          {tourId ? 'Publish & search' : 'Search'}
        </button>
      </form>

      {published.data?.need || poolPublished.data?.need ? (
        <div className="mb-4 rounded-md border border-ovation-accent/30 bg-ovation-accent/5 p-4 text-sm text-ovation-ink">
          {(published.data ?? poolPublished.data)!.need.label
            ? `Crew need: ${(published.data ?? poolPublished.data)!.need.label}`
            : `Crew search for ${(published.data ?? poolPublished.data)!.need.role.replace(/_/g, ' ')}`}
          {' · '}
          {new Date((published.data ?? poolPublished.data)!.need.dateFrom).toLocaleDateString()} –{' '}
          {new Date((published.data ?? poolPublished.data)!.need.dateTo).toLocaleDateString()}
          {(published.data ?? poolPublished.data)!.need.venueCity
            ? ` · ${(published.data ?? poolPublished.data)!.need.venueCity}`
            : ''}
          {' · '}
          {(published.data ?? poolPublished.data)!.matches.length} matches
        </div>
      ) : null}

      {statusMessage ? (
        <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          {statusMessage}
        </div>
      ) : null}

      {searchKey === 0 ? (
        <div className="rounded-md border border-ovation-ink/10 bg-white p-6 text-sm text-ovation-muted">
          Pick a role and search manually, or select a tour to publish a crew need using its dates
          and locations.
        </div>
      ) : (
        <AsyncBoundary isLoading={activeLoading} error={activeError}>
          <DataTable
            rows={rows}
            rowKey={(r) => r.crewProfileId}
            emptyMessage="No crew available for those parameters. Try widening the date range."
            columns={[
              { key: 'name', header: 'Name', render: (r) => r.displayName },
              {
                key: 'role',
                header: 'Primary role',
                render: (r) => r.primaryRole.replace(/_/g, ' '),
              },
              {
                key: 'base',
                header: 'Base',
                render: (r) => `${r.baseCity}, ${r.baseCountry}`,
              },
              {
                key: 'distance',
                header: 'Distance',
                align: 'right',
                render: (r) =>
                  r.distanceKm != null ? `${Math.round(r.distanceKm)} km` : '—',
              },
              {
                key: 'shows',
                header: 'Prior shows',
                align: 'right',
                render: (r) => r.showsForArtistCount,
              },
              {
                key: 'internal',
                header: 'Internal reviews',
                align: 'right',
                render: (r) =>
                  formatReview(r.internalReviewCount, r.internalReviewAverage),
              },
              {
                key: 'external',
                header: 'External reviews',
                align: 'right',
                render: (r) =>
                  formatReview(r.externalReviewCount, r.externalReviewAverage),
              },
              {
                key: 'rate',
                header: 'Day rate',
                align: 'right',
                render: (r) =>
                  r.dayRateMinor != null
                    ? `£${(r.dayRateMinor / 100).toLocaleString()}`
                    : '—',
              },
              {
                key: 'actions',
                header: 'Actions',
                render: (r) => {
                  const candidate = candidateByCrew.get(r.crewProfileId)
                  const busy =
                    candidateMutation.isPending || offerMutation.isPending
                  const pipelineStatus = candidateStatusLabel(candidate?.status)

                  return (
                    <div className="flex flex-wrap items-center gap-2">
                      {pipelineStatus ? (
                        <span className="rounded bg-ovation-ink/5 px-2 py-1 text-xs text-ovation-ink">
                          {pipelineStatus}
                        </span>
                      ) : null}
                      {activeNeedId ? (
                        <>
                          <button
                            type="button"
                            disabled={
                              busy ||
                              candidate?.status === 'shortlisted' ||
                              candidate?.status === 'interview_requested' ||
                              candidate?.status === 'interested' ||
                              candidate?.status === 'declined'
                            }
                            onClick={() =>
                              candidateMutation.mutate({
                                needId: activeNeedId,
                                crewProfileId: r.crewProfileId,
                                action: 'shortlist',
                              })
                            }
                            className="rounded border border-ovation-ink/20 px-2 py-1 text-xs hover:bg-ovation-ink/5 disabled:opacity-50"
                          >
                            Shortlist
                          </button>
                          <button
                            type="button"
                            disabled={
                              busy ||
                              candidate?.status === 'interview_requested' ||
                              candidate?.status === 'interested' ||
                              candidate?.status === 'declined'
                            }
                            onClick={() =>
                              candidateMutation.mutate({
                                needId: activeNeedId,
                                crewProfileId: r.crewProfileId,
                                action: 'request_interview',
                              })
                            }
                            className="rounded border border-ovation-accent/40 px-2 py-1 text-xs text-ovation-accent hover:bg-ovation-accent/10 disabled:opacity-50"
                          >
                            Request interview
                          </button>
                        </>
                      ) : null}
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void handleSendOffer(r.crewProfileId)}
                        className="rounded bg-ovation-ink px-2 py-1 text-xs text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
                      >
                        Send offer
                      </button>
                    </div>
                  )
                },
              },
            ]}
          />
        </AsyncBoundary>
      )}
    </div>
  )
}
